function isDefined (value) {
  return value !== undefined && value !== null
}

function trimTrailingSlash (url) {
  return String(url || '').replace(/\/+$/, '')
}

function pickQuery (input, keys) {
  const query = {}
  for (const key of keys) {
    if (isDefined(input[key])) {
      query[key] = input[key]
    }
  }
  return query
}

function buildQueryString (query) {
  const sp = new URLSearchParams()
  Object.entries(query || {}).forEach(([k, v]) => {
    if (!isDefined(v)) {
      return
    }
    if (Array.isArray(v)) {
      v.forEach((item) => {
        if (isDefined(item)) {
          sp.append(k, String(item))
        }
      })
      return
    }
    sp.set(k, String(v))
  })
  const queryString = sp.toString()
  return queryString ? `?${queryString}` : ''
}

function withPathParams (template, params) {
  // console.log('template', template.replace(/\{(\w+)\}/g, (_, key) =>
    // encodeURIComponent(String(params[key]))))
  return template.replace(/\{(\w+)\}/g, (_, key) =>
    encodeURIComponent(String(params[key]))
  )
}

async function parseJsonSafely (response) {
  if (response.status === 204) {
    return null
  }
  const text = await response.text()
  if (!text) {
    return null
  }
  try {
    return JSON.parse(text)
  } catch (error) {
    return text
  }
}

async function request (baseUrl, method, path, options = {}) {
  const url =
    `${trimTrailingSlash(baseUrl)}${path}` + buildQueryString(options.query)
  const response = await fetch(url, {
    method,
    headers: Object.assign(
      {},
      isDefined(options.body) ? { 'Content-Type': 'application/json' } : {},
      options.headers || {}
    ),
    body: isDefined(options.body) ? JSON.stringify(options.body) : undefined,
    signal: options.signal
  })

  const data = await parseJsonSafely(response)
  if (!response.ok) {
    const message =
      (data && data.message) ||
      (data && data.error && data.error.message) ||
      response.statusText ||
      'Request failed'
    const err = new Error(message)
    err.status = response.status
    err.data = data
    throw err
  }

  return { data }
}

/**
 * 可 async-for-await 的 SSE 流；不用 async generator，避免未走 babel 的 node_modules 在旧 webpack 下解析失败。
 */
function createSseStream (response, onSseError) {
  const queue = []
  let readerFinished = false
  let readError = null
  let waitingResolve = null
  let waitingReject = null

  function pushEventBlock (block) {
    const lines = block.split(/\r?\n/)
    const dataLines = []
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i]
      if (line.indexOf('data:') === 0) {
        dataLines.push(line.slice(5).replace(/^\s+/, ''))
      }
    }
    if (dataLines.length === 0) {
      return null
    }
    const rawData = dataLines.join('\n')
    if (!rawData || rawData === '[DONE]') {
      return null
    }
    try {
      return JSON.parse(rawData)
    } catch (error) {
      if (typeof onSseError === 'function') {
        onSseError(error)
      }
      return { type: 'sse.parse.error', raw: rawData }
    }
  }

  function pushValue (v) {
    if (waitingResolve) {
      const r = waitingResolve
      waitingResolve = null
      waitingReject = null
      r({ value: v, done: false })
    } else {
      queue.push(v)
    }
  }

  /** 仅在流读取结束（正常或异常）后调用，唤醒阻塞在 next() 上的消费者 */
  function resolveWaiting () {
    if (!waitingResolve) {
      return
    }
    const r = waitingResolve
    waitingResolve = null
    waitingReject = null
    if (queue.length > 0) {
      r({ value: queue.shift(), done: false })
    } else {
      r({ value: undefined, done: true })
    }
  }

  function finishReader () {
    readerFinished = true
    resolveWaiting()
  }

  function runConsumer () {
    if (!response.body) {
      finishReader()
      return
    }
    const decoder = new TextDecoder('utf-8')
    let buffer = ''
    const reader = response.body.getReader()

    function pump () {
      return reader.read().then(function (result) {
        if (result.done) {
          if (buffer.trim()) {
            const parsed = pushEventBlock(buffer)
            if (parsed != null) {
              pushValue(parsed)
            }
          }
          finishReader()
          return
        }
        buffer += decoder.decode(result.value, { stream: true })
        const blocks = buffer.split(/\r?\n\r?\n/)
        buffer = blocks.pop() || ''
        for (let i = 0; i < blocks.length; i++) {
          const parsed = pushEventBlock(blocks[i])
          if (parsed != null) {
            pushValue(parsed)
          }
        }
        return pump()
      }).catch(function (err) {
        readError = err
        if (waitingResolve && waitingReject) {
          const rj = waitingReject
          waitingResolve = null
          waitingReject = null
          rj(err)
        }
        finishReader()
      })
    }

    pump()
  }

  runConsumer()

  return {
    [Symbol.asyncIterator]: function () {
      return {
        next: function () {
          if (readError) {
            return Promise.reject(readError)
          }
          if (queue.length > 0) {
            return Promise.resolve({ value: queue.shift(), done: false })
          }
          if (readerFinished) {
            return Promise.resolve({ value: undefined, done: true })
          }
          return new Promise(function (resolve, reject) {
            waitingResolve = resolve
            waitingReject = reject
          }).then(function (result) {
            if (readError) {
              return Promise.reject(readError)
            }
            // pushValue / resolveWaiting 通过 resolve(IteratorResult) 唤醒时，必须把该结果原样交给 for-await
            if (result && typeof result.done === 'boolean') {
              return result
            }
            if (queue.length > 0) {
              return { value: queue.shift(), done: false }
            }
            return { value: undefined, done: true }
          })
        }
      }
    }
  }
}

function normalizeQueryPayload (payload) {
  const root = payload || {}
  const nested = root.query && typeof root.query === 'object' ? root.query : {}
  return Object.assign({}, nested, root)
}

export function createOpencodeClient (options = {}) {
  const baseUrl = trimTrailingSlash(options.baseUrl || '')
  const defaultDirectory = options.directory
  // const defaultWorkspace = options.workspace

  const withDefaults = (input) => {
    const src = normalizeQueryPayload(input)
    return Object.assign({}, src, {
      directory: isDefined(src.directory) ? src.directory : defaultDirectory
    // workspace: isDefined(src.workspace) ? src.workspace : defaultWorkspace
    })
  }

  const client = {
    global: {
      // 订阅全局 SSE 事件流。
      async event (opts = {}) {
        const payload = withDefaults(opts)
        const query = pickQuery(payload, ['directory', 'workspace'])
        const url = `${baseUrl}/global/event${buildQueryString(query)}`
        const response = await fetch(url, {
          method: 'GET',
          headers: { Accept: 'text/event-stream' },
          signal: payload.signal
        })
        if (!response.ok) {
          const text = await response.text()
          throw new Error(text || response.statusText || 'SSE connection failed')
        }
        return {
          stream: createSseStream(response, payload.onSseError)
        }
      }
    },
    project: {
      // 获取项目列表。
      list (params = {}) {
        const payload = withDefaults(params)
        return request(baseUrl, 'GET', '/project', {
          query: pickQuery(payload, ['directory', 'workspace'])
        })
      },
      // 获取当前活跃项目。
      current (params = {}) {
        const payload = withDefaults(params)
        return request(baseUrl, 'GET', '/project/current', {
          query: pickQuery(payload, ['directory', 'workspace'])
        })
      }
    },
    app: {
      // 获取可用 Agent 列表。
      agents (params = {}) {
        const payload = withDefaults(params)
        return request(baseUrl, 'GET', '/agent', {
          query: pickQuery(payload, ['directory', 'workspace'])
        })
      }
    },
    config: {
      // 获取 Provider 配置与默认模型信息。
      providers (params = {}) {
        const payload = withDefaults(params)
        return request(baseUrl, 'GET', '/config/providers', {
          query: pickQuery(payload, ['directory', 'workspace'])
        })
      },
      // 获取当前项目配置。
      get (params = {}) {
        const payload = withDefaults(params)
        return request(baseUrl, 'GET', '/config', {
          query: pickQuery(payload, ['directory', 'workspace'])
        })
      },
      // 更新当前项目配置。
      update (params = {}) {
        const payload = withDefaults(params)
        const body = payload.config || payload.body || {}
        return request(baseUrl, 'PATCH', '/config', {
          query: pickQuery(payload, ['directory', 'workspace']),
          body: { config: body }
        })
      }
    },
    provider: {
      // 获取可用 Provider 列表。
      list (params = {}) {
        const payload = withDefaults(params)
        return request(baseUrl, 'GET', '/provider', {
          query: pickQuery(payload, ['directory', 'workspace'])
        })
      }
    },
    command: {
      // 获取系统命令列表。
      list (params = {}) {
        const payload = withDefaults(params)
        return request(baseUrl, 'GET', '/command', {
          query: pickQuery(payload, ['directory', 'workspace'])
        })
      }
    },
    question: {
      // 回答待确认问题（多选答案）。
      reply (params = {}) {
        const payload = withDefaults(params)
        return request(
          baseUrl,
          'POST',
          withPathParams('/question/{requestID}/reply', payload),
          {
            query: pickQuery(payload, ['directory', 'workspace']),
            body: { answers: payload.answers || [] }
          }
        )
      },
      // 拒绝待确认问题。
      reject (params = {}) {
        const payload = withDefaults(params)
        return request(
          baseUrl,
          'POST',
          withPathParams('/question/{requestID}/reject', payload),
          {
            query: pickQuery(payload, ['directory', 'workspace'])
          }
        )
      }
    },
    find: {
      // 在工作区中按文本模式搜索。
      text (params = {}) {
        const payload = withDefaults(params)
        return request(baseUrl, 'GET', '/find', {
          query: pickQuery(payload, ['directory', 'workspace', 'pattern'])
        })
      },
      // 在工作区中按名称搜索文件/目录。
      files (params = {}) {
        const payload = withDefaults(params)
        return request(baseUrl, 'GET', '/find/file', {
          query: pickQuery(payload, [
            'directory',
            'workspace',
            'query',
            'dirs',
            'type',
            'limit'
          ])
        })
      }
    },
    file: {
      // 读取指定文件内容。
      read (params = {}) {
        const payload = withDefaults(params)
        return request(baseUrl, 'GET', '/file/content', {
          query: pickQuery(payload, ['directory', 'workspace', 'path'])
        })
      }
    },
    session: {
      // 获取指定会话详情。
      get (params = {}) {
        const payload = withDefaults(params)
        return request(
          baseUrl,
          'GET',
          withPathParams('/session/{sessionID}', payload),
          {
            query: pickQuery(payload, ['directory', 'workspace'])
          }
        )
      },
      // 获取会话列表。
      list (params = {}) {
        const payload = withDefaults(params)
        return request(baseUrl, 'GET', '/session', {
          query: pickQuery(payload, ['directory', 'workspace', 'search', 'limit'])
        })
      },
      // 创建新会话。
      create (params = {}) {
        const payload = withDefaults(params)
        return request(baseUrl, 'POST', '/session', {
          query: pickQuery(payload, ['directory', 'workspace']),
          body: {
            parentID: payload.parentID,
            title: payload.title,
            permission: payload.permission,
            workspaceID: payload.workspaceID
          }
        })
      },
      // 删除指定会话。
      delete (params = {}) {
        const payload = withDefaults(params)
        // console.log('payload', withPathParams('/session/{sessionID}', payload))
        return request(
          baseUrl,
          'DELETE',
          withPathParams('/session/{sessionID}', payload),
          {
            query: pickQuery(payload, ['directory', 'workspace'])
          }
        )
      },
      // 更新会话信息（例如标题）。
      update (params = {}) {
        const payload = withDefaults(params)
        return request(
          baseUrl,
          'PATCH',
          withPathParams('/session/{sessionID}', payload),
          {
            query: pickQuery(payload, ['directory', 'workspace']),
            body: { title: payload.title, time: payload.time }
          }
        )
      },
      // 触发会话总结。
      summarize (params = {}) {
        const payload = withDefaults(params)
        return request(
          baseUrl,
          'POST',
          withPathParams('/session/{sessionID}/summarize', payload),
          {
            query: pickQuery(payload, ['directory', 'workspace']),
            body: {
              providerID: payload.providerID,
              modelID: payload.modelID,
              auto: payload.auto
            }
          }
        )
      },
      // 获取会话消息列表。
      messages (params = {}) {
        const payload = withDefaults(params)
        return request(
          baseUrl,
          'GET',
          withPathParams('/session/{sessionID}/message', payload),
          {
            query: pickQuery(payload, ['directory', 'workspace', 'limit', 'before'])
          }
        )
      },
      // 同步发送消息并等待回复。
      prompt (params = {}) {
        const payload = withDefaults(params)
        return request(
          baseUrl,
          'POST',
          withPathParams('/session/{sessionID}/message', payload),
          {
            query: pickQuery(payload, ['directory', 'workspace']),
            body: {
              messageID: payload.messageID,
              model: payload.model,
              agent: payload.agent,
              noReply: payload.noReply,
              tools: payload.tools,
              format: payload.format,
              system: payload.system,
              variant: payload.variant,
              parts: payload.parts
            }
          }
        )
      },
      // 异步发送消息，立即返回。
      promptAsync (params = {}) {
        const payload = withDefaults(params)
        return request(
          baseUrl,
          'POST',
          withPathParams('/session/{sessionID}/prompt_async', payload),
          {
            query: pickQuery(payload, ['directory', 'workspace']),
            body: {
              messageID: payload.messageID,
              model: payload.model,
              agent: payload.agent,
              noReply: payload.noReply,
              tools: payload.tools,
              format: payload.format,
              system: payload.system,
              variant: payload.variant,
              parts: payload.parts
            }
          }
        )
      },
      // 初始化会话上下文（分析项目并注入初始信息）。
      init (params = {}) {
        const payload = withDefaults(params)
        return request(
          baseUrl,
          'POST',
          withPathParams('/session/{sessionID}/init', payload),
          {
            query: pickQuery(payload, ['directory', 'workspace']),
            body: {
              providerID: payload.providerID,
              modelID: payload.modelID,
              messageID: payload.messageID
            }
          }
        )
      },
      // 中止正在执行的会话任务。
      abort (params = {}) {
        const payload = withDefaults(params)
        return request(
          baseUrl,
          'POST',
          withPathParams('/session/{sessionID}/abort', payload),
          {
            query: pickQuery(payload, ['directory', 'workspace'])
          }
        )
      },
      // 分享会话，生成可访问链接。
      share (params = {}) {
        const payload = withDefaults(params)
        return request(
          baseUrl,
          'POST',
          withPathParams('/session/{sessionID}/share', payload),
          {
            query: pickQuery(payload, ['directory', 'workspace'])
          }
        )
      },
      // 取消会话分享。
      unshare (params = {}) {
        const payload = withDefaults(params)
        return request(
          baseUrl,
          'DELETE',
          withPathParams('/session/{sessionID}/share', payload),
          {
            query: pickQuery(payload, ['directory', 'workspace'])
          }
        )
      },
      // 向会话发送命令（如 /list）。
      command (params = {}) {
        const payload = withDefaults(params)
        return request(
          baseUrl,
          'POST',
          withPathParams('/session/{sessionID}/command', payload),
          {
            query: pickQuery(payload, ['directory', 'workspace']),
            body: {
              messageID: payload.messageID,
              agent: payload.agent,
              model: payload.model,
              arguments: payload.arguments,
              command: payload.command,
              variant: payload.variant,
              parts: payload.parts
            }
          }
        )
      },
      // 撤回到指定消息或指定 part 状态。
      revert (params = {}) {
        const payload = withDefaults(params)
        return request(
          baseUrl,
          'POST',
          withPathParams('/session/{sessionID}/revert', payload),
          {
            query: pickQuery(payload, ['directory', 'workspace']),
            body: {
              messageID: payload.messageID,
              partID: payload.partID
            }
          }
        )
      }
    }
  }

  return client
}
